import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeft } from "lucide-react";
import i18n from "../../i18n";

const LowerAustriaCase = ({ imageName = "lower_austria.jpg" }) => {
  const [langTick, setLangTick] = useState(0);

  useEffect(() => {
    const onLang = () => setLangTick((x) => x + 1);
    i18n.on("languageChanged", onLang);
    return () => i18n.off("languageChanged", onLang);
  }, []);

  const tr = useMemo(() => {
    void langTick;

    return {
      back: i18n.t("lowerAustriaCase.back"),

      orgTitle: i18n.t("lowerAustriaCase.org.title"),
      orgDesc: i18n.t("lowerAustriaCase.org.description"),
      overallDuration: i18n.t("lowerAustriaCase.org.duration"),

      durationLabel: i18n.t("lowerAustriaCase.labels.duration"),
      toolsLabel: i18n.t("lowerAustriaCase.labels.tools"),

      intro: {
        title: i18n.t("lowerAustriaCase.intro.title"),
        text: i18n.t("lowerAustriaCase.intro.text"),
      },

      modern: {
        duration: i18n.t("lowerAustriaCase.modern.duration"),
        title: i18n.t("lowerAustriaCase.modern.title"),
        text: i18n.t("lowerAustriaCase.modern.text"),
        bullets: [
          i18n.t("lowerAustriaCase.modern.bullets.0"),
          i18n.t("lowerAustriaCase.modern.bullets.1"),
          i18n.t("lowerAustriaCase.modern.bullets.2"),
        ],
      },

      infra: {
        duration: i18n.t("lowerAustriaCase.infra.duration"),
        title: i18n.t("lowerAustriaCase.infra.title"),
        text: i18n.t("lowerAustriaCase.infra.text"),
        bullets: [
          i18n.t("lowerAustriaCase.infra.bullets.0"),
          i18n.t("lowerAustriaCase.infra.bullets.1"),
          i18n.t("lowerAustriaCase.infra.bullets.2"),
        ],
      },

      legacy: {
        duration: i18n.t("lowerAustriaCase.legacy.duration"),
        title: i18n.t("lowerAustriaCase.legacy.title"),
        text: i18n.t("lowerAustriaCase.legacy.text"),
        bullets: [
          i18n.t("lowerAustriaCase.legacy.bullets.0"),
          i18n.t("lowerAustriaCase.legacy.bullets.1"),
        ],
      },
    };
  }, [langTick]);

  const tools = useMemo(
    () => ["Java", "Microfocus", "Angular", "MS-SQL", "Windows"],
    []
  );

  const toolMap = useMemo(
    () => ({
      Java: "java.png",
      Microfocus: "microfocus.png",
      Angular: "angular.png",
      "MS-SQL": "sql.png",
      Windows: "microsoft.png",
    }),
    []
  );

  const handleBack = () => window.history.back();
  const imageUrl = `${process.env.PUBLIC_URL}/images/${imageName}`;

  return (
    <div>
      <div
        className="relative h-64 w-full bg-cover bg-center flex items-start justify-start"
        style={{ backgroundImage: `url(${imageUrl})` }}
      >
        <div className="max-w-screen-xl w-full">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-gray-700 text-base font-medium py-2 px-4 bg-white/70 hover:bg-white rounded-full m-8"
            type="button"
          >
            <ArrowLeft className="w-5 h-5" />
            {tr.back}
          </button>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-8 p-4 md:p-8">
        <div className="w-full md:w-[320px]">
          <div className="p-4 flex flex-col gap-4">
            <img
              src={`${process.env.PUBLIC_URL}/logos/no.png`}
              alt="Lower Austria Logo"
              className="h-14 object-contain self-start"
            />

            <div>
              <h2 className="text-lg font-semibold">{tr.orgTitle}</h2>
              <p className="text-sm text-gray-600">{tr.orgDesc}</p>
            </div>

            <div>
              <div className="text-sm text-gray-400">{tr.durationLabel}</div>
              <div className="text-sm font-medium">{tr.overallDuration}</div>
            </div>

            <div className="mt-6">
              <div className="text-sm text-gray-400 mb-2">{tr.toolsLabel}</div>
              <div className="flex flex-col gap-2">
                {tools.map((tool) => {
                  const iconFile = `${process.env.PUBLIC_URL}/tools/${
                    toolMap[tool] || `${tool.toLowerCase()}.png`
                  }`;

                  return (
                    <div
                      key={tool}
                      className="flex items-center gap-3 px-5 py-2 bg-white rounded-full shadow text-sm font-medium w-[160px]"
                    >
                      <img
                        src={iconFile}
                        alt={`${tool} icon`}
                        className="w-7 h-7 object-contain"
                      />
                      {tool}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1">
          <div className="bg-white px-6 py-8 space-y-10 rounded-xl">
            <section className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-800">
                {tr.intro.title}
              </h2>
              <p>{tr.intro.text}</p>
            </section>

            {[tr.modern, tr.infra, tr.legacy].map((block, idx) => (
              <section
                key={idx}
                className="bg-gray-100 p-6 rounded-xl space-y-4"
              >
                <p className="text-sm text-gray-500 font-medium">
                  {tr.durationLabel} {block.duration}
                </p>
                <h3 className="text-lg font-semibold">{block.title}</h3>
                <p>{block.text}</p>
                <ul className="list-disc list-inside text-gray-800">
                  {block.bullets.map((b, i) => (
                    <li key={i}>{b}</li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LowerAustriaCase;







